import { randomUUID } from "node:crypto";
import type { NativeApprovalDescription } from "@skaile/workspaces/types";
import type { CodexServerRequestHandlers } from "../codex-app-server.js";
import type { AgentEvent } from "../types.js";
import type { NativeServerRequests } from "./native-protocol.generated.js";

type InputRequest = NativeServerRequests["item/tool/requestUserInput"];

/** One correlated native question batch, settled only by matching replies or cancellation. */
export class CodexQuestions {
  private pending?: {
    id: string;
    questions: InputRequest["params"]["questions"];
    answers: InputRequest["result"]["answers"];
    approval?: NativeApprovalDescription;
    finish: (result?: InputRequest["result"]) => void;
  };
  constructor(private readonly emit: (event: AgentEvent) => void) {}

  readonly request: NonNullable<CodexServerRequestHandlers["item/tool/requestUserInput"]> = async (
    params,
    signal,
  ) => this.park(params, signal);

  /** Park native consent; MCP uses only local correlation because it supplies no native item ID. */
  async requestApproval(
    identity: Pick<InputRequest["params"], "itemId" | "threadId" | "turnId"> | undefined,
    approval: NativeApprovalDescription,
    signal: AbortSignal,
  ): Promise<boolean> {
    const result = await this.park(
      {
        ...identity,
        isBlocking: true,
        questions: [
          {
            id: "native-approval",
            header: "Approval",
            question:
              approval.kind === "permissions"
                ? "Allow these permissions for the current turn?"
                : approval.kind === "mcp"
                  ? "Allow this native MCP request once?"
                  : "Allow this native action once?",
            options: [
              {
                label: "Approve once",
                description:
                  approval.kind === "permissions"
                    ? "Allow for the current turn."
                    : "Allow only this request.",
              },
              { label: "Deny", description: "Do not allow this request." },
            ],
          },
        ],
      },
      signal,
      approval,
    );
    return result.answers["native-approval"]?.answers[0] === "Approve once";
  }

  private async park(
    params: Omit<InputRequest["params"], "itemId" | "threadId" | "turnId"> & { itemId?: string },
    signal: AbortSignal,
    approval?: NativeApprovalDescription,
  ): Promise<InputRequest["result"]> {
    signal.throwIfAborted();
    if (
      this.pending ||
      !params.isBlocking ||
      !Array.isArray(params.questions) ||
      params.questions.length === 0 ||
      params.questions.length > 16 ||
      params.questions.some((q) => !q.id || !q.question || q.isSecret) ||
      new Set(params.questions.map((q) => q.id)).size !== params.questions.length ||
      new Set(params.questions.map((q) => q.question)).size !== params.questions.length
    )
      throw new Error("CODEX_INPUT_UNSUPPORTED");
    params = structuredClone(params);
    approval = approval ? structuredClone(approval) : undefined;
    const id = randomUUID();
    const eventId = params.itemId ?? `local-consent:${id}`;
    return new Promise((resolve, reject) => {
      const abort = () => finish();
      const finish = (result?: InputRequest["result"]) => {
        if (this.pending?.id !== id) return;
        this.pending = undefined;
        signal.removeEventListener("abort", abort);
        if (result && !signal.aborted) resolve(result);
        else reject(new Error("CODEX_INPUT_CANCELLED"));
      };
      this.pending = {
        id,
        questions: params.questions,
        answers: Object.create(null),
        finish,
        approval,
      };
      const input = {
        requestId: id,
        questions: structuredClone(params.questions),
        ...(approval ? { approval: structuredClone(approval) } : {}),
      };
      signal.addEventListener("abort", abort, { once: true });
      if (signal.aborted) {
        abort();
        return;
      }
      this.emit({
        type: "message_start",
        message: {
          role: "assistant",
          content: [
            {
              type: "tool_use",
              name: "AskUserQuestion",
              id: eventId,
              input,
            },
          ],
        },
      });
      if (this.pending?.id !== id || signal.aborted) return;
      this.emit({
        type: "tool_call",
        name: "AskUserQuestion",
        tool: { name: "AskUserQuestion" },
        toolCallId: eventId,
        input,
      });
    });
  }

  get active(): boolean {
    return Boolean(this.pending);
  }

  answer(answer: string, question?: string, requestId?: string): boolean {
    const pending = this.pending;
    if (!pending || pending.id !== requestId || typeof answer !== "string") return false;
    if (
      pending.approval &&
      (question !== pending.questions[0]?.question || !["Approve once", "Deny"].includes(answer))
    )
      return false;
    const q =
      question === undefined
        ? pending.questions.find((entry) => !Object.hasOwn(pending.answers, entry.id))
        : pending.questions.find((entry) => entry.question === question);
    if (!q || Object.hasOwn(pending.answers, q.id)) return false;
    pending.answers[q.id] = { answers: [answer] };
    if (pending.questions.every((entry) => Object.hasOwn(pending.answers, entry.id)))
      pending.finish({ answers: pending.answers });
    return true;
  }
}
